import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import StepBarArrows from "../../components/navigation/navigationElements/StepBarArrows";
import Breadcrumb from "../../components/navigation/Breadcrumb";
import { CampaignOptimisationType, CampaignPacing, CampaignPriceModel, CampaignPriorityType, CampaignTargetType, CampaignType, DealCampaignType, DealStatus } from "../../__generated__/graphql";
import { getAllowedCampaignTypes, useCurrentUser } from "../../utils/CurrentUserContext";
import { GET_DEAL_BY_ID } from "../../utils/api/preferredDeal";
import Spinner from "../../components/icons/Spinner";

const getTabs = (isNativeDisplay: boolean) => {
  const tabs = [
    { name: "Details", to: "", id: "1" },
    { name: "Websites", to: "select-websites", id: "2" },
  ];

  return isNativeDisplay ? tabs : [...tabs, { name: "Content", to: "create-meta-article", id: "3" }];
}

const CreateCampaignRoot = ({isNativeDisplay} : {isNativeDisplay: boolean}) => {
  const {user} = useCurrentUser()
  const [searchParams] = useSearchParams()
  const dealId = searchParams.get('deal')

  const { data: dealData, loading: dealLoading } = useQuery(GET_DEAL_BY_ID, {
    variables: { id: dealId },
    skip: !dealId,
  })
  const deal = dealData?.deal

  const allowedCampaignTypes = getAllowedCampaignTypes(user)
  const nativeDisplayTypes = allowedCampaignTypes.nativeDisplay || []
  const nativeContentTypes = allowedCampaignTypes.nativeContent || []
  // Pick the first available type; fall back to any other allowed type or a safe default to avoid crashes during impersonation
  const defaultType =
    (isNativeDisplay ? nativeDisplayTypes[0]?.value : nativeContentTypes[0]?.value) ??
    nativeDisplayTypes[0]?.value ??
    nativeContentTypes[0]?.value ??
    CampaignType.InHouse
  const tabs = getTabs(isNativeDisplay)

  const [campaignDetails, setCampaignDetails] = useState({
    pacing: CampaignPacing.Even,
    country: 'Denmark',
    targetType: CampaignTargetType.Budget,
    priority: CampaignPriorityType.StandardMedium,
    rateCard: isNativeDisplay ? {priceModel: CampaignPriceModel.Cpc} : {priceModel: CampaignPriceModel.Cpr},
    type: defaultType,
    optimisation: isNativeDisplay ? CampaignOptimisationType.Ctr : CampaignOptimisationType.Reads,
    sensitiveCategory: 'NOT_SENSITIVE',
  });

  const [campaign, setCampaign] = useState(null);
  const [selectedWebsiteIds, setSelectedWebsiteIds] = useState<string[]>([]);

  // When deal data loads, pre-fill campaign details and website selection
  useEffect(() => {
    if (deal) {
      setCampaignDetails(prev => ({
        ...prev,
        advertiserId: deal.advertiserId,
        type: deal.campaignType === DealCampaignType.Display ? CampaignType.MarketplaceNativeDisplay : CampaignType.Standard,
      }))
      setSelectedWebsiteIds(deal.websiteIds || [])
    }
  }, [deal, defaultType])

  const pages = [
    isNativeDisplay
    ? { name: "Create Native Display Campaign", href: "/create/display-campaign/", current: true }
    : { name: "Create Native Content Campaign", href: "/create/campaign/", current: true }
  ];

  if (dealId && dealLoading) {
    return (
      <main>
        <Breadcrumb pages={pages} />
        <div className="mt-8 flex justify-center">
          <Spinner />
        </div>
      </main>
    )
  }

  if (deal?.status === DealStatus.Used && deal.campaignId) {
    return <Navigate to={`/campaigns/${deal.campaignId}/details`} replace />
  }


  if (deal) {
    const shouldBeDisplay = deal.campaignType === DealCampaignType.Display
    if (shouldBeDisplay && !isNativeDisplay) {
      return <Navigate to={`/create/display-campaign/?deal=${dealId}`} replace />
    }
    if (!shouldBeDisplay && isNativeDisplay) {
      return <Navigate to={`/create/campaign/?deal=${dealId}`} replace />
    }
  }

  return (
    <main>
      <Breadcrumb pages={pages} />
      <div>
        <div className="mt-8 grid lg:grid-cols-3 gap-6 sm:grid-cols-1">
          <div className="space-y-6 col-start-1 col-span-3">
            <StepBarArrows tabs={tabs} />
            <section>
              <div className="flex-1 relative bg-white rounded-md py-6 px-6 overflow-y-auto focus:outline-none">
                <Outlet
                  context={{
                    campaign,
                    setCampaign,
                    campaignDetails: [campaignDetails, setCampaignDetails],
                    selectedWebsiteIds: [selectedWebsiteIds, setSelectedWebsiteIds],
                    deal,
                  }}
                />
              </div>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
};

export default CreateCampaignRoot;
